import type { ChangeEvent } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import type {
  TextControlBaseInstance,
  TextControlProps
} from '@elfsight/widget-ui';
import { TextControl } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  scrollbarColoredMixin
} from '@elfsight/widget-utils';
import { commonFieldsSettings } from '../commonFieldSettings';

export type LongTextProps = TextControlProps & {
  showLabel?: boolean;
  fieldLabelColor?: string;
  fieldLabelPosition?: 'inside' | 'outside';
  fieldLabelFontStyle?: TextStyleValue;
  fieldColor?: string;
  fieldFillColor?: string;
  fieldFontStyle?: TextStyleValue;
  fieldBorderType?: 'underline' | 'outline' | null;
  fieldBorderColor?: string;
  fieldBorderWidth?: number;
  fieldBorderRadius?: number;
  onChange?: (
    value: string,
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  maxVisibleRows?: number;
};

export const LongText = forwardRef<TextControlBaseInstance, LongTextProps>(
  (
    {
      value,
      label,
      showLabel = true,
      fieldLabelColor = commonFieldsSettings.fieldLabelColor,
      fieldLabelPosition = commonFieldsSettings.fieldLabelPosition,
      fieldLabelFontStyle = commonFieldsSettings.fieldLabelFontStyle,
      fieldColor = commonFieldsSettings.fieldColor,
      fieldFillColor = commonFieldsSettings.fieldFillColor,
      fieldFontStyle = commonFieldsSettings.fieldFontStyle,
      fieldBorderType = commonFieldsSettings.fieldBorderType,
      fieldBorderColor = commonFieldsSettings.fieldBorderColor,
      fieldBorderWidth = commonFieldsSettings.fieldBorderWidth,
      fieldBorderRadius = commonFieldsSettings.fieldBorderRadius,
      maxVisibleRows = 6,
      onChange,
      className,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const minVisibleRows = fieldBorderType === 'underline' ? 1 : 3;
    const ariaLabel = typeof label === 'string' ? label : undefined;

    return (
      <LongTextControl
        {...forwardingProps}
        ref={forwardingRef}
        className={createReadableClass(
          {
            component: 'fields',
            element: 'long-text'
          },
          [className]
        )}
        initialValue={value}
        ariaLabel={ariaLabel}
        label={showLabel && fieldLabelPosition === 'inside' ? label : undefined}
        labelColor={fieldLabelColor}
        labelFont={fieldLabelFontStyle}
        color={fieldColor}
        background={fieldFillColor}
        font={fieldFontStyle}
        borderType={fieldBorderType ? fieldBorderType : undefined}
        borderColor={fieldBorderColor}
        borderWidth={fieldBorderWidth}
        borderRadius={fieldBorderRadius}
        onValueChange={onChange}
        minVisibleRows={minVisibleRows}
        maxVisibleRows={maxVisibleRows}
        multiline
      />
    );
  }
);

const LongTextControl = styled(TextControl)`
  textarea {
    margin: 0 !important;
    background-color: transparent !important;

    ${({ color = 'rgb(17, 17, 17)' }) => css`
      @supports not selector(-webkit-scrollbar) {
        scrollbar-width: thin;
        scrollbar-color: ${getColorWithAlpha(color, 0.1)} transparent;
      }

      ${scrollbarColoredMixin(getColorWithAlpha(color, 0.1))};

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${getColorWithAlpha(color, 0.3)};
      }
    `}
  }
`;
